import { Injectable, inject } from '@angular/core';
import { DocumentationStore } from '../stores/documentation.store';
import {
  AdditionalDocument,
  EventDocumentation,
  EventAttachment,
  Upload,
} from '../models/documentation.model';
import { BuildingPlan } from '../models/building-plan.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentationService {
  private documentationStore = inject(DocumentationStore);

  // ==================== Expose Store Signals ====================
  readonly isLoading = this.documentationStore.loading;
  readonly uploadProgress = this.documentationStore.uploadProgress;
  readonly uploadState = this.documentationStore.uploadState;
  readonly buildingPlan = this.documentationStore.buildingPlan;
  readonly additionalDocuments = this.documentationStore.additionalDocuments;
  readonly currentObjectId = this.documentationStore.currentObjectId;
  readonly currentSensorId = this.documentationStore.currentSensorId;

  // ==================== Computed Signals ====================
  readonly hasBuildingPlan = this.documentationStore.hasBuildingPlan;
  readonly hasAdditionalDocuments = this.documentationStore.hasAdditionalDocuments;
  readonly additionalDocumentsCount = this.documentationStore.additionalDocumentsCount;
  readonly isUploading = this.documentationStore.isUploading;
  readonly hasEventDocumentation = this.documentationStore.hasEventDocumentation;
  readonly eventAttachmentsCount = this.documentationStore.eventAttachmentsCount;

  // ==================== Event Documentation Signals ====================
  readonly eventDocumentation = this.documentationStore.eventDocumentation;
  readonly eventAttachments = this.documentationStore.eventAttachments;

  // ==================== Building Plan Methods ====================

  async uploadBuildingPlan(
    objectId: number,
    fileName: string,
    fileData: string
  ): Promise<BuildingPlan> {
    return this.documentationStore.uploadBuildingPlan(objectId, fileName, fileData);
  }

  async getBuildingPlan(objectId: number): Promise<BuildingPlan | null> {
    return this.documentationStore.getBuildingPlan(objectId);
  }

  /**
   * Delete the building plan for an object
   * @param objectId - The object ID
   * @returns Promise with success status
   */
  async deleteBuildingPlan(objectId: number): Promise<boolean> {
    return this.documentationStore.deleteBuildingPlan(objectId);
  }

  // ==================== Additional Document Methods ====================

  async uploadAdditionalDocument(
    objectId: number,
    sensorId: number,
    fileName: string,
    fileData: string
  ): Promise<AdditionalDocument> {
    return this.documentationStore.uploadAdditionalDocument(objectId, sensorId, fileName, fileData);
  }

  async getAdditionalDocuments(sensorId: number): Promise<AdditionalDocument[]> {
    return this.documentationStore.getAdditionalDocuments(sensorId);
  }

  async renameAdditionalDocument(documentId: number, name: string): Promise<AdditionalDocument> {
    return this.documentationStore.renameAdditionalDocument(documentId, name);
  }

  async deleteAdditionalDocument(documentId: number): Promise<boolean> {
    return this.documentationStore.deleteAdditionalDocument(documentId);
  }

  // ==================== State Management ====================

  /**
   * Reset the upload state
   */
  resetUploadState(): void {
    this.documentationStore.resetUploadState();
  }

  /**
   * Clear all state
   */
  clear(): void {
    this.documentationStore.clear();
  }

  // ==================== Event Documentation Methods ====================

  /**
   * Get or create unfinished event documentation for an asset
   * @param assetId - Asset ID
   * @param sensorId - Optional sensor ID
   * @returns Promise with event documentation
   */
  async getUnfinishedDocumentation(
    assetId: number,
    sensorId?: number
  ): Promise<EventDocumentation> {
    return this.documentationStore.getUnfinishedDocumentation(assetId, sensorId);
  }

  /**
   * Finish event documentation and write to event history
   * @param documentationId - Documentation ID to finish
   * @returns Promise with success status
   */
  async finishDocumentation(documentationId: number): Promise<boolean> {
    return this.documentationStore.finishDocumentation(documentationId);
  }

  /**
   * Update event documentation details
   * @param documentation - Documentation object to update
   * @returns Promise with updated documentation
   */
  async updateEventDocumentation(documentation: EventDocumentation): Promise<EventDocumentation> {
    return this.documentationStore.updateDocumentation(documentation);
  }

  /**
   * Upload attachment for event documentation
   * @param documentationId - Documentation ID
   * @param attachmentType - Type of attachment
   * @param fileName - File name
   * @param fileData - Base64 encoded file data
   * @returns Promise with uploaded attachment
   */
  async uploadEventAttachment(
    documentationId: number,
    attachmentType: string,
    fileName: string,
    fileData: string
  ): Promise<EventAttachment> {
    return this.documentationStore.uploadEventAttachment(
      documentationId,
      attachmentType,
      fileName,
      fileData
    );
  }

  /**
   * Delete event documentation attachment
   * @param attachmentId - Attachment ID to delete
   * @returns Promise with success status
   */
  async deleteEventAttachment(attachmentId: number): Promise<boolean> {
    return this.documentationStore.deleteEventAttachment(attachmentId);
  }

  // ==================== Upload Progress Tracking ====================

  async traceUploadProgress(upload: any): Promise<Upload> {
    return this.documentationStore.traceUploadProgress(upload);
  }
}
